import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { savingsService } from '../services/savings';
import { benefitsService } from '../services/benefits';
import { PlanSwipe } from '../components/PlanSwipe';
import { FlipCard } from '../components/ui/FlipCard';

const pesos = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;

/**
 * `PANTALLA_PREPAGO` — el momento de venta.
 *
 * Se llega aquí justo después de cerrar la felicitación del canje: el cliente
 * acaba de comprobar con dinero real que VONDI le ahorra, y es cuando tiene
 * más sentido ofrecerle la membresía.
 *
 * La comparativa «ahorro − costo = ganancia» solo aparece cuando el ahorro
 * **cubre** el plan sugerido. Si no lo cubre, la resta daría negativo y sería
 * un pésimo argumento; en ese caso se enseña solo cuánto lleva ahorrado
 * (decisión de Ricardo, 11-ago-2026).
 */
export default function Prepago() {
  const navigate = useNavigate();
  const [ahorro, setAhorro] = useState(null);
  const [bloqueados, setBloqueados] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const [res, estado, catalogo] = await Promise.all([
          savingsService.summary(),
          benefitsService.getUnlockState(),
          benefitsService.getAll().catch(() => null),
        ]);
        if (!vigente) return;
        setAhorro(res?.data ?? null);

        // Una tarjeta bloqueada bajo el bloque de precio, como en el mockup:
        // es el recordatorio de lo que se está perdiendo.
        const abiertos = estado?.unlockedBenefitIds ?? [];
        setBloqueados(
          (catalogo?.data ?? []).filter((b) => !abiertos.includes(String(b.id))).slice(0, 2)
        );
      } finally {
        if (vigente) setCargando(false);
      }
    })();
    return () => {
      vigente = false;
    };
  }, []);

  const m = ahorro?.membership;

  return (
    <div style={{ minHeight: '100vh', background: '#fff', paddingBottom: 60 }}>
      <div style={{ padding: 'max(64px, env(safe-area-inset-top, 64px)) 28px 0' }}>
        <h1
          className="font-display"
          style={{
            fontSize: 'clamp(30px, 9vw, 40px)',
            fontWeight: 700,
            letterSpacing: '-0.03em',
            color: '#0E0E10',
            margin: 0,
            lineHeight: 1.1,
          }}
        >
          Tu ahorro <span style={{ color: '#6E6E77' }}>acumulado</span>
        </h1>

        <div
          className="font-display"
          style={{
            fontSize: 'clamp(44px, 14vw, 62px)',
            fontWeight: 700,
            letterSpacing: '-0.04em',
            margin: '10px 0 22px',
            backgroundImage: 'linear-gradient(100deg, #F52B16, #FA759F)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
          }}
        >
          {cargando ? '—' : pesos(ahorro?.total)}
        </div>

        {/* La comparativa: solo si el ahorro ya cubre el plan. */}
        {!cargando && m?.coversIt && (
          <div
            style={{
              background: '#fff',
              border: '1px solid #E7E7EC',
              borderRadius: 22,
              padding: '16px 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              boxShadow: '0 10px 30px -14px rgba(250,77,10,0.28)',
              marginBottom: 18,
            }}
          >
            <Cifra titulo="Tu ahorro" valor={pesos(ahorro.total)} color="#F73E48" />
            <span style={{ color: '#6E6E77', fontSize: 18 }}>−</span>
            <Cifra titulo="costo" valor={pesos(m.price)} tachado />
            <span style={{ color: '#6E6E77', fontSize: 18 }}>=</span>
            <Cifra titulo="llevarías de ganancia" valor={`+${pesos(m.gain)}`} color="#1A9C52" />
          </div>
        )}

        {/* Sin cubrir el plan, no se resta: solo se celebra lo que lleva. */}
        {!cargando && ahorro?.hasData && !m?.coversIt && (
          <p style={{ fontSize: 14, color: '#6E6E77', lineHeight: 1.5, margin: '0 0 18px' }}>
            {/* Decía «con la membresía»; Iván pidió sacar esa palabra del lado
                del usuario (13-ago-2026). */}
            Ya llevas {pesos(ahorro.total)} ahorrados con un solo beneficio. Con tu
            acceso a vondi se te abren todas las marcas.
          </p>
        )}

        {!cargando && !ahorro?.hasData && (
          <p style={{ fontSize: 14, color: '#6E6E77', lineHeight: 1.5, margin: '0 0 18px' }}>
            Aquí vas a ver cuánto llevas ahorrado en cuanto uses tu primer beneficio en tienda.
          </p>
        )}

        <PlanSwipe
          planSugerido={m?.suggestedPlan || 'annual'}
          onElegir={(plan) => navigate('/subscription', { state: { plan: plan.key } })}
        />
      </div>

      {/* Lo que se está perdiendo */}
      {bloqueados.length > 0 && (
        <div style={{ padding: '26px 28px 0', display: 'flex', gap: 14, overflowX: 'auto' }} className="mp-no-scrollbar">
          {bloqueados.map((b) => (
            <div key={b.id} style={{ flex: '0 0 62%', maxWidth: 240 }}>
              <FlipCard benefit={b} estado="bloqueada" flippable={false} />
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate('/home')}
        style={{
          display: 'block',
          margin: '26px auto 0',
          background: 'transparent',
          border: 'none',
          color: '#6E6E77',
          fontSize: 13.5,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Ahora no
      </button>
    </div>
  );
}

function Cifra({ titulo, valor, color = '#0E0E10', tachado = false }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 0 }}>
      <div style={{ fontSize: 10.5, color: '#6E6E77', marginBottom: 2, whiteSpace: 'nowrap' }}>{titulo}</div>
      <div
        className="font-display"
        style={{
          fontSize: 17,
          fontWeight: 700,
          color,
          textDecoration: tachado ? 'line-through' : 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {valor}
      </div>
    </div>
  );
}
